// CarOS - Client-seitige Bildaufbereitung für Foto-Uploads (Mobile-Workflow)
//
// - Kompression NUR für die Upload-/Arbeitsversion (max. 2560px lange Kante,
//   JPEG ~87% - kein sichtbarer Verlust bei Lack, Felgen, Cockpit, Details)
// - EXIF-Orientierung wird eingebrannt (Hochformat bleibt Hochformat)
// - HEIC/HEIF (iPhone): Browser kann es nicht dekodieren -> roh übertragen,
//   der Server konvertiert (pillow-heif) und bewahrt die Originaldatei auf

const MAX_DIM = 2560;
const JPEG_QUALITY = 0.87;

export const isHeicFile = (file) =>
  /image\/hei[cf]/i.test(file?.type || "") || /\.hei[cf]$/i.test(file?.name || "");

// Datei UNVERÄNDERT als data-URL lesen (keine Canvas-Dekodierung)
export const fileToRawDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
    reader.readAsDataURL(file);
  });

const loadDecodedImage = async (file) => {
  // Bevorzugt: createImageBitmap mit EXIF-Orientierung (Chrome 81+, Safari 15+)
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (e) { /* Fallback unten */ }
  }
  // Fallback: <img> - moderne Browser wenden die EXIF-Orientierung automatisch an
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode-failed")); };
    img.src = url;
  });
};

/**
 * Bereitet eine Bilddatei für den Upload vor.
 * Rückgabe: { dataUrl, filename, heic }
 */
export const prepareImageForUpload = async (file) => {
  // HEIC/HEIF: roh senden - Server konvertiert und liefert bei Problemen
  // eine verständliche Fehlermeldung (Einzel-Retry möglich)
  if (isHeicFile(file)) {
    return { dataUrl: await fileToRawDataUrl(file), filename: file.name || "foto.heic", heic: true };
  }

  try {
    const src = await loadDecodedImage(file);
    const w = src.width || src.naturalWidth;
    const h = src.height || src.naturalHeight;
    if (!w || !h) throw new Error("decode-failed");

    const scale = Math.min(1, MAX_DIM / Math.max(w, h));
    const cw = Math.round(w * scale);
    const ch = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff"; // PNG-Transparenz -> weiß statt schwarz
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(src, 0, 0, cw, ch);
    if (typeof src.close === "function") src.close();

    const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    // Sicherheitsnetz: wenn die "Kompression" die Datei deutlich vergrößert
    // und das Original klein genug ist, lieber das Original senden
    if (scale === 1 && file.size < 1_500_000 && dataUrl.length > file.size * 1.9) {
      return { dataUrl: await fileToRawDataUrl(file), filename: file.name || "foto.jpg", heic: false };
    }
    const base = (file.name || "foto").replace(/\.(png|webp|heic|heif|jpe?g)$/i, "");
    return { dataUrl, filename: `${base}.jpg`, heic: false };
  } catch (e) {
    // Dekodierung fehlgeschlagen -> roh senden, Server normalisiert
    // oder liefert eine klare Fehlermeldung
    return { dataUrl: await fileToRawDataUrl(file), filename: file.name || "foto", heic: false };
  }
};
